import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { workProductCardClassName } from '@/components/work-product-card/work-product-card-styles'
import { useLocation } from 'react-router-dom'
import { WorkProductCardContent, WorkProductCardContents, WorkProductCardIcon } from '@/components/work-product-card/WorkProductCardParts'
import { useContext } from 'react'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { TableEntrySource } from '@/analytics/enums/table.enums'
import { trackTableOpened } from '@/analytics/utils/table-analytics'
import { TableTitlesContext } from './table-titles-context'
import { useTableCardTitle } from '../hooks/useTableCardTitle'
import { TableNavigationContext } from './TableNavigationContext'
import { parseTableLink } from '../utils/table-links'
import { Table } from '@/assets/icons'

/** The same compact visual treatment as a generated draft, retaining native link behavior. */
export default function TableChatCard({ href }: { href: string }) {
  const { enabled, loading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.TABLES)
  if (loading || !enabled) return <span>Table unavailable</span>
  return <EnabledTableChatCard href={href} />
}

function EnabledTableChatCard({ href }: { href: string }) {
  const location = useLocation()
  const navigation = useContext(TableNavigationContext)
  const titles = useContext(TableTitlesContext)
  const { trackEvent } = useAnalytics()
  const table = parseTableLink(href)
  const current = parseTableLink(location.pathname)
  const active = !!table && current?.matterId === table.matterId && current.tableId === table.tableId
  const { title, unavailable } = useTableCardTitle(table?.matterId, table?.tableId)
  if (unavailable) {
    return (
      <span
        data-work-product-card
        className="my-2 grid grid-cols-[60px_minmax(0,1fr)] items-center min-h-[62px] w-full max-w-[420px] rounded-[5px] overflow-hidden border border-brand-neutral-300 bg-brand-neutral-50 text-brand-neutral-600"
      >
        <WorkProductCardIcon active={false}>
          <Table className="w-6 h-6" aria-hidden="true" />
        </WorkProductCardIcon>
        <WorkProductCardContent>
          <span className="text-[13px] text-left font-medium leading-tight">{title}</span>
        </WorkProductCardContent>
      </span>
    )
  }
  return (
    <a
      data-work-product-card
      href={table?.href ?? href}
      onClick={(event) => {
        if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return
        if (!table || navigation?.matterId !== table.matterId) return
        event.preventDefault()
        const record = titles?.tablesByMatter[table.matterId]?.find((item) => item.id === table.tableId)
        trackTableOpened(trackEvent, {
          tableId: table.tableId,
          matterId: table.matterId,
          tableEntrySource: TableEntrySource.Chat,
          ...(record ? { documentAnalysis: record.document_analysis } : {}),
        })
        navigation.openTable(table.tableId)
      }}
      aria-current={active ? 'true' : undefined}
      className={`${workProductCardClassName(active)} my-2 no-underline text-brand-neutral-950`}
    >
      <WorkProductCardContents active={active} icon={<Table className="w-6 h-6" aria-hidden="true" />} title={title} />
    </a>
  )
}
